/**
 * Wishlist — Clue Wear
 *
 * Handles two responsibilities:
 *   1. Bookmark buttons (.wishlist-btn) on product pages / quick-buy / sticky bar.
 *   2. The wishlist page (sections/main-wishlist.liquid) grid render + actions.
 *
 * i18n strings are injected by Liquid into a <script type="application/json"> block
 * so this file stays language-agnostic and works for EN, AR, and any future locale.
 *
 * Storage key: 'clue_wishlist'  (localStorage, array of {id, title, url, image})
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'clue_wishlist';

  // ── i18n ─────────────────────────────────────────────────────────────────
  // Snippet injects: added, removed, view_list, aria_add, aria_remove
  // Page injects:    view, remove, clear_list, confirm_clear, whatsapp_intro
  function readI18n(elementId) {
    var el = document.getElementById(elementId);
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  // ── Storage helpers ───────────────────────────────────────────────────────
  function getList() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function saveList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
  }

  function isInList(productId) {
    return getList().some(function (item) {
      return String(item.id) === String(productId);
    });
  }

  function addToList(product) {
    var list = getList();
    if (!isInList(product.id)) {
      list.push(product);
      saveList(list);
    }
  }

  function removeFromList(productId) {
    saveList(
      getList().filter(function (item) {
        return String(item.id) !== String(productId);
      })
    );
  }

  // ── Toast (snippet context) ───────────────────────────────────────────────
  function showToast(message, includeLink, i18n) {
    var toast = document.getElementById('wishlist-toast');
    if (!toast) return;

    toast.innerHTML = message;

    if (includeLink) {
      var link = document.createElement('a');
      link.href = '/pages/wishlist';
      link.textContent = i18n.view_list || 'View list';
      toast.appendChild(link);
    }

    toast.classList.add('is-visible');
    setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 3500);
  }

  // ── Button state helpers ──────────────────────────────────────────────────
  function updateButtons(productId, saved, i18n) {
    document
      .querySelectorAll('.wishlist-btn[data-product-id="' + productId + '"]')
      .forEach(function (btn) {
        btn.classList.toggle('is-saved', saved);
        btn.setAttribute(
          'aria-label',
          saved
            ? (i18n.aria_remove || 'Remove from Wishlist')
            : (i18n.aria_add   || 'Wishlist')
        );

        var labelEl = btn.querySelector('.wishlist-btn__label');
        if (labelEl) {
          labelEl.textContent = saved
            ? (btn.dataset.labelSaved || i18n.aria_remove || 'In Wishlist')
            : (btn.dataset.labelAdd   || i18n.aria_add   || 'Add to Wishlist');
        }
      });
  }

  // ── Header count helper ───────────────────────────────────────────────────
  function updateHeaderCount() {
    const threshold = 10;
    const count = getList().length;
    const counter = count > threshold ? `${threshold}+` : `${count}`;
    document.querySelectorAll('[data-consider-later-count], [data-wishlist-count]').forEach(function (el) {
      el.textContent = counter;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // ── Button init ───────────────────────────────────────────────────────────
  function initButtons() {
    var i18n = readI18n('wishlist-i18n');

    document.querySelectorAll('.wishlist-btn').forEach(function (btn) {
      var productId = btn.dataset.productId;

      // Set initial saved state
      if (isInList(productId)) {
        btn.classList.add('is-saved');
        btn.setAttribute('aria-label', i18n.aria_remove || 'Remove from Wishlist');
        var labelEl = btn.querySelector('.wishlist-btn__label');
        if (labelEl) {
          labelEl.textContent =
            btn.dataset.labelSaved || i18n.aria_remove || 'In Wishlist';
        }
      }

      // Avoid double-binding
      if (btn.dataset.clBound) return;
      btn.dataset.clBound = 'true';

      btn.addEventListener('click', function (e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        var id    = btn.dataset.productId;
        var saved = isInList(id);

        if (saved) {
          removeFromList(id);
          updateButtons(id, false, i18n);
          showToast(i18n.removed || 'Removed from Wishlist', false, i18n);
        } else {
          addToList({
            id:    id,
            title: btn.dataset.productTitle,
            url:   btn.dataset.productUrl,
            image: btn.dataset.productImage
          });
          updateButtons(id, true, i18n);
          showToast(i18n.added || 'Added to Wishlist —', true, i18n);
        }

        document.dispatchEvent(new CustomEvent('wishlist:updated'));
      });
    });
  }

  // ── Page render (main-wishlist section) ─────────────────────────────
  function initPage() {
    var grid     = document.getElementById('wishlist-grid');
    if (!grid) return; // not on the wishlist page

    var i18n     = readI18n('wishlist-page-i18n');
    var empty    = document.getElementById('wishlist-empty');
    var actions  = document.getElementById('wishlist-actions');
    var waBtn    = document.getElementById('wishlist-whatsapp');
    var clearBtn = document.getElementById('wishlist-clear');

    function buildWhatsAppMessage(list) {
      var intro = i18n.whatsapp_intro || 'My Wishlist from Clue Wear:';
      var lines = list.map(function (item) {
        return '\u2022 ' + item.title + '\n  ' + item.url;
      });
      return 'https://wa.me/?text=' + encodeURIComponent(intro + '\n\n' + lines.join('\n\n'));
    }

    function renderPage() {
      var list       = getList();
      var viewLabel  = i18n.view   || 'View';
      var rmLabel    = i18n.remove || 'Remove';

      if (list.length === 0) {
        grid.innerHTML = '';
        if (empty)   empty.style.display   = 'block';
        if (actions) actions.style.display = 'none';
        return;
      }

      if (empty)   empty.style.display   = 'none';
      if (actions) actions.style.display = 'flex';
      if (waBtn)   waBtn.href = buildWhatsAppMessage(list);

      grid.innerHTML = list.map(function (item) {
        var imageHtml = item.image
          ? '<img src="' + item.image + '" alt="' +
            item.title.replace(/"/g, '&quot;') +
            '" class="wishlist-card__image" loading="lazy">'
          : '<div class="wishlist-card__image-placeholder">&#9729;</div>';

        return (
          '<div class="wishlist-card" data-product-id="' + item.id + '">' +
            '<a href="' + item.url + '" class="wishlist-card__link">'+
              '<div class="wishlist-card__image-wrapper">' + imageHtml + '</div>' +
              '<div class="wishlist-card__info">' +
                '<p class="wishlist-card__title">' +
                  '<a href="' + item.url + '">' + item.title + '</a>' +
                '</p>' +
                '<div class="wishlist-card__actions">' +
                  '<a href="' + item.url + '" class="wishlist-card__view">' + viewLabel + '</a>' +
                  '<button class="wishlist-card__remove" data-id="' + item.id + '">' + rmLabel + '</button>' +
                '</div>' +
              '</div>' +
            '</a>'+ 
          '</div>'
        );
      }).join('');

      grid.querySelectorAll('.wishlist-card__remove').forEach(function (btn) {
        btn.addEventListener('click', function () {
          removeFromList(btn.dataset.id);
          renderPage();
        });
      });

      if (clearBtn) {
        clearBtn.onclick = function () {
          if (confirm(i18n.confirm_clear || 'Clear your entire Wishlist?')) {
            saveList([]);
            renderPage();
          }
        };
      }
    }

    renderPage();
    document.addEventListener('wishlist:updated', renderPage);
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    initButtons();
    initPage();
    updateHeaderCount();
  });

  document.addEventListener('wishlist:updated', updateHeaderCount);

  // Re-init buttons after Focal quick-buy injection
  document.addEventListener('quick-buy:loaded', initButtons);
  document.addEventListener('wishlist:reinit', initButtons);

  // MutationObserver — catch dynamically injected buttons
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        var btns = node.querySelectorAll
          ? node.querySelectorAll('.wishlist-btn')
          : [];
        if (btns.length > 0 || (node.classList && node.classList.contains('wishlist-btn'))) {
          initButtons();
        }
      });
    });
  });

  document.addEventListener('DOMContentLoaded', function () {
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  });
})();